import { gql, useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import Modal, { ModalBody, ModalFooter } from '../../../../components/Modal';
import { Flex, Text } from 'rebass';
import Button from '../../../../components/Button/Button';
import PropTypes from 'prop-types';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import { Textarea } from '@rebass/forms';

type ApproveTestReportsModalProps = {
  onClose: (actionTaken?: boolean) => void;
  action: 'APPROVE' | 'REJECT';
  jobIds: string[] | readonly string[];
};

const APPROVE_BACKLOW_TESTS = gql`
  mutation ApproveBackflowTests($jobIds: [ID!]!) {
    approveBackflowTests(jobIds: $jobIds) {
      jobId
      waterSupplierStatus
    }
  }
`;

const REJECT_BACKLOW_TESTS = gql`
  mutation RejectBackflowTests($jobIds: [ID!]!, $comment: String!) {
    rejectBackflowTests(jobIds: $jobIds, comment: $comment) {
      jobId
      waterSupplierStatus
    }
  }
`;

const ApproveTestReportsModal: React.FC<ApproveTestReportsModalProps> = ({
  action,
  onClose,
  jobIds,
}) => {
  const onCancel = useCallback(() => onClose(), []);
  const onActionComplete = useCallback(() => onClose(true), []);

  const [
    updateTestReports,
    { loading: mutationLoading, error: mutationError },
  ] = useMutation(
    action === 'APPROVE' ? APPROVE_BACKLOW_TESTS : REJECT_BACKLOW_TESTS,
    {
      onCompleted: onActionComplete,
      onError: useCallback((error) => {
        console.error(
          `Error ${
            action === 'APPROVE' ? 'approving' : 'rejecting'
          } backflow tests`,
          error
        );
      }, []),
    }
  );

  const [comment, setComment] = useState<string>('');
  const onChange = useCallback(
    (e: any) => {
      setComment(e.target.value);
    },
    [setComment]
  );

  return (
    <Modal
      title={`${constantToLabel(action)} Test Report${
        jobIds.length > 1 ? 's' : ''
      }`}
      onClose={onCancel}
    >
      <ModalBody>
        <Flex p={3} flexDirection="column">
          <Text pb={5} variant="body1">
            {action} the {jobIds.length > 1 ? jobIds.length : ''} selected test
            report
            {jobIds.length > 1 ? 's' : ''}?
          </Text>
          {action === 'REJECT' && (
            <Textarea
              height={3 * 16 + 2}
              placeholder="Enter comment"
              autoFocus
              onChange={onChange}
            />
          )}
          {mutationLoading && <p>Loading...</p>}
          {mutationError && <p>Error :( {mutationError.message}</p>}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon={action === 'APPROVE' ? 'check-circle' : 'slash-circle'}
          disabled={action === 'REJECT' && !comment}
          onClick={() => {
            updateTestReports({
              variables: {
                jobIds: jobIds,
                comment: action === 'REJECT' ? comment : undefined,
              },
            });
          }}
        >
          {constantToLabel(action)}
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ApproveTestReportsModal.propTypes = {
  onClose: PropTypes.any,
  jobIds: PropTypes.any.isRequired,
};

export default ApproveTestReportsModal;
